/**
 * analytics.ts
 * ──────────────────────────────────────────────────────────────
 * PostHog wrapper for the React web app.
 *
 * Activates ONLY when VITE_POSTHOG_KEY is set at build time. With a blank
 * key, every exported function is a no-op — no network calls, no cookies,
 * and posthog-js is never fetched at all.
 *
 * LOADED LAZILY, AND THAT IS THE POINT. This module is imported by 14
 * components for `track()`. While it also carried a static
 * `import posthog from 'posthog-js'`, that pulled the whole SDK into the
 * eager graph, and Vite emitted it as a modulepreload in index.html. The
 * critical path was ~226 kB gzipped before a student could see anything,
 * and analytics-vendor was the single largest piece of it, bigger than
 * React. On a 360px phone over Mozambican mobile data that is seconds of
 * blank screen: p75 LCP on /subjects measured 3.6s, worst sample 7.2s.
 *
 * So posthog-js is now a dynamic import inside initAnalytics(), and calls
 * made before it resolves are buffered rather than dropped. Nothing in the
 * app needs analytics to render, and nothing should wait for it.
 *
 * Design decisions:
 *   - Autocapture is DISABLED. At 10K users, autocapture's every-click /
 *     every-pageview would blow past the 1M-event free tier in days.
 *     Every event we want is named and captured explicitly.
 *   - Session recording is DISABLED for the same reason + privacy.
 *   - `identify()` pushes persistent properties (province, grade, target
 *     programme, onboarding flow) so cohorts can slice retention and
 *     engagement curves without joining back to a server DB.
 *   - Never sends student name / email / phone. Only the opaque student id
 *     and non-PII cohort dimensions.
 */
import type posthogT from 'posthog-js'

const apiKey = import.meta.env.VITE_POSTHOG_KEY ?? ''
const host   = import.meta.env.VITE_POSTHOG_HOST ?? 'https://eu.posthog.com'

type PostHog = typeof posthogT

let client: PostHog | null = null
let loading: Promise<void> | null = null

/**
 * Calls made between app start and the SDK arriving.
 *
 * Bounded on purpose. Without a cap, a student who never gets analytics
 * loaded (blocked, offline, ad-blocker) would accumulate events for the
 * whole session. 50 is far more than the handful fired before idle.
 */
const MAX_BUFFER = 50
const buffer: Array<(p: PostHog) => void> = []

function enqueue(fn: (p: PostHog) => void): void {
  if (!apiKey) return
  if (client) { fn(client); return }
  if (buffer.length < MAX_BUFFER) buffer.push(fn)
}

/**
 * Load and start PostHog. Safe to call more than once.
 *
 * Deliberately returns a promise nobody has to await: callers fire it when
 * the app is idle and carry on. A failure here is silent by design, because
 * a student must never see a blank screen over an analytics SDK.
 */
export function initAnalytics(): Promise<void> {
  if (!apiKey) return Promise.resolve()
  if (loading) return loading

  loading = import('posthog-js')
    .then(({ default: posthog }) => {
      posthog.init(apiKey, {
        api_host:              host,
        capture_pageview:      false,            // we emit explicit page_view events instead
        capture_pageleave:     true,
        autocapture:           false,            // volume control — free tier 1M/mo
        disable_session_recording: true,         // privacy + volume
        persistence:           'localStorage+cookie',
      })
      client = posthog
      // Drain in order, so a page_view fired during boot still precedes the
      // events that followed it.
      for (const fn of buffer.splice(0)) {
        try { fn(posthog) } catch { /* one bad event must not lose the rest */ }
      }
    })
    .catch(() => {
      // Blocked by an extension, offline, or the CDN is down. Drop the
      // buffer so it cannot grow for the rest of the session.
      buffer.length = 0
    })

  return loading
}

export function track(event: string, properties?: Record<string, unknown>): void {
  enqueue(p => p.capture(event, properties))
}

/**
 * Attach persistent cohort properties to a student. Cohort slicers in the
 * PostHog UI read these — so at Year 2 pitch time you can filter
 * "D30 retention for students in Nampula with UEM as target" without a
 * join back to your DB.
 *
 * Pass null (or nothing) to reset identification on logout.
 */
export function identify(studentId: string | null, properties?: Record<string, unknown>): void {
  enqueue(p => {
    if (!studentId) { p.reset(); return }
    p.identify(studentId, properties)
  })
}

/** Explicit page view event — call on route change. */
export function trackPageView(path: string): void {
  enqueue(p => p.capture('page_view', { path }))
}

export const isAnalyticsEnabled = !!apiKey
